import React from 'react'

import type {
  TDerivedMetricPreviewDto,
  TDerivedMetricUnit
} from '@homepal/api-contract'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'

import BarChart from 'components_new/molecules/charts/BarChart'
import SelectSearch from 'components_new/molecules/SelectSearch'

import GenericWidget from 'components_new/organisms/GenericWidget'

import { AUTO_SIGNIFICANT_FIGURES } from 'utils/autoFormat'
import { useInternalCustomers } from 'redux/hooks/internal/Customers'

const CHART_HEIGHT = 260

interface PreviewProps {
  name: string
  onSelectCustomer: (customerId: string) => void
  preview: TDerivedMetricPreviewDto | null
  previewing: boolean
  selectedCustomerId: string | null
  unit: TDerivedMetricUnit | null
}

/**
 * The definition computed for one customer, month by month over the last year,
 * in the widget shell it will live in.
 *
 * Bars rather than a line: a monthly outcome is a bucket, and a month an
 * operand has no data for reads as an empty slot instead of a gap a line
 * draws through — which is how a definition built on a half-mapped slice
 * gives itself away.
 */
const Preview = (props: PreviewProps) => {
  const {
    name,
    onSelectCustomer,
    preview,
    previewing,
    selectedCustomerId,
    unit
  } = props

  const { customerOptions } = useInternalCustomers()

  const series = preview?.series ?? []

  // An unconfigured slice empties the series too, and the warning above
  // already says so.
  const empty =
    !previewing && !!preview && !preview.missing_slice && series.length === 0

  return (
    <Stack direction="column" sx={{ gap: 2 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}
      >
        <Box sx={{ minWidth: 260 }}>
          <SelectSearch
            closeOnSelect
            disableClearable={true}
            externalLabel="Testkund"
            multiple={false}
            onChange={(value) => onSelectCustomer(value.id)}
            options={customerOptions}
            placeholder="Välj kund"
            selected={
              customerOptions.find(
                (option) => option.id === selectedCustomerId
              ) ?? null
            }
            size="small"
          />
        </Box>
        {preview?.missing_slice ? (
          <Alert severity="warning" sx={{ flex: 1, minWidth: 280 }}>
            {`Skärningen "${preview.missing_slice}" är inte konfigurerad ` +
              'för den valda kunden'}
          </Alert>
        ) : null}
      </Stack>

      {selectedCustomerId ? (
        <Box sx={{ height: CHART_HEIGHT }}>
          <GenericWidget
            disableFullsize={true}
            disableSubtitle={true}
            editMode={false}
            title={name || 'Nytt mätetal'}
          >
            <BarChart
              data={{
                datasets: [
                  {
                    label: name,
                    main: true,
                    type: 'bar',
                    unit,
                    values: series.map((point) => point.value)
                  }
                ],
                labels: series.map((point) => point.label ?? '')
              }}
              flexItem={true}
              loading={previewing}
              significantFigures={AUTO_SIGNIFICANT_FIGURES}
              variant="detailed"
            />
          </GenericWidget>
        </Box>
      ) : null}

      {empty ? (
        <Text color="text.secondary" variant="caption">
          Inget värde för den valda kunden och perioden
        </Text>
      ) : null}
    </Stack>
  )
}

export default Preview
